import { useState } from "react";
import { toast } from "sonner";
import { FileSpreadsheet, Loader2, RefreshCw, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useApi, useLoad } from "@/lib/api";
import { gbp, fmtDate, thisMonth } from "@/lib/format";
import { PageHead, Empty } from "@/components/Bits";
import StatementLines from "@/components/StatementLines";
import { RefLinks } from "@/components/RefLinks";

function Result({ r }) {
  if (!r) return null;
  return (
    <div className="rounded-2xl border border-teal-200 bg-teal-50 p-4 text-sm text-teal-900 rise" data-testid="reconcile-result">
      <p className="font-display text-lg font-bold">{r.supplier} · {r.month}</p>
      <p>{r.issues} discrepanc{r.issues === 1 ? "y" : "ies"} found · {r.new.length} new quer{r.new.length === 1 ? "y" : "ies"} {r.supplier_has_no_email ? "raised (not emailed)" : "emailed"} · {r.auto_resolved.length} auto-resolved</p>
      {r.new.length > 0 && <p className="mt-1 font-mono text-xs">{r.new.join("  ")}</p>}
      {r.links_pending > 0 && <p className="mt-2 font-semibold text-sky-800" data-testid="reconcile-links-pending">{r.links_pending} delivery note{r.links_pending === 1 ? "" : "s"} to link to an invoice number (see the top of the page).</p>}
      {r.supplier_has_no_email && <p className="mt-2 font-semibold text-amber-700" data-testid="no-supplier-email-warning">No email saved for this wholesaler — add one on the Wholesalers page so queries reach them.</p>}
    </div>
  );
}

function Coverage({ s }) {
  const [open, setOpen] = useState(false);
  const c = s.coverage;
  if (!c?.lines) return null;
  const gaps = c.missing.length + c.not_listed.length;
  const id = `${s.supplier}-${s.month}`;
  return (
    <div className="mt-2">
      <div className="flex flex-wrap items-center gap-2">
        <div className="h-1.5 w-28 overflow-hidden rounded-full bg-slate-100"><div className={`h-full ${c.with_photo === c.lines ? "bg-emerald-500" : "bg-amber-500"}`} style={{ width: `${(100 * c.with_photo) / c.lines}%` }} /></div>
        <span className="text-xs font-semibold text-slate-600" data-testid={`coverage-${id}`}>{c.with_photo} of {c.lines} invoice lines have a photo</span>
        {gaps > 0 && <button type="button" onClick={() => setOpen(!open)} className="text-xs font-semibold text-teal-700 underline" data-testid={`coverage-toggle-${id}`}>{open ? "Hide gaps" : `Show ${gaps} gap${gaps === 1 ? "" : "s"}`}</button>}
      </div>
      {open && (
        <div className="mt-2 grid gap-3 text-xs sm:grid-cols-2" data-testid={`coverage-gaps-${id}`}>
          {c.missing.length > 0 && <div><p className="mb-1 font-semibold text-rose-700">On the statement, no photo saved</p>
            {c.missing.map((m) => <p key={m.ref} className="font-mono">{m.ref} · {gbp(m.amount)}{m.linking ? <span className="ml-1 font-sans text-sky-700">(delivery note to link)</span> : ""}</p>)}</div>}
          {c.not_listed.length > 0 && <div><p className="mb-1 font-semibold text-amber-700">Saved, but not on this statement</p>
            {c.not_listed.map((m) => <p key={m.ref} className="font-mono">{m.ref} · {m.date} · {gbp(m.amount)}</p>)}</div>}
        </div>
      )}
    </div>
  );
}

export default function StatementsPage() {
  const api = useApi();
  const suppliers = useLoad("/suppliers");
  const list = useLoad("/statements");
  const [supplier, setSupplier] = useState("");
  const [month, setMonth] = useState(thisMonth());
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [lines, setLines] = useState(null);
  const [reading, setReading] = useState(false);
  const [linksKey, setLinksKey] = useState(0);

  const pickFile = async (e) => {
    const fl = e.target.files?.[0] || null;
    setFile(fl); setLines(null);
    if (!fl || /\.csv$/i.test(fl.name)) return;
    setReading(true);
    try {
      const fd = new FormData(); fd.append("file", fl);
      const d = await api("post", "/statements/read", fd);
      setLines(d.lines.map((l) => ({ type: l.kind, ref: l.ref, amount: (l.amount / 100).toFixed(2), ...(l.dn ? { dn: l.dn } : {}) })));
      if (d.supplier && !supplier) setSupplier(d.supplier);
      if (d.month) setMonth(d.month);
      toast.success(`Read ${d.lines.length} lines — please check them`);
    } catch (err) { toast.error(err.message); setFile(null); e.target.value = ""; } finally { setReading(false); }
  };

  const reconcile = async (s, m) => {
    const r = await api("post", "/reconcile", { supplier: s, month: m });
    setResult(r); list.reload(); setLinksKey((k) => k + 1);
    toast.success(`Reconciled: ${r.new.length} new quer${r.new.length === 1 ? "y" : "ies"}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Choose the statement (CSV, PDF or photo)");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("supplier", supplier); fd.append("month", month);
      if (lines) fd.append("lines_json", JSON.stringify(lines.filter((l) => l.ref.trim())));
      else fd.append("csv_file", file);
      const s = await api("post", "/statements", fd);
      toast.success(`Imported ${s.rows} rows`);
      await reconcile(s.supplier, s.month);
      setFile(null); setLines(null); e.target.reset();
    } catch (err) { toast.error(err.message); } finally { setBusy(false); }
  };

  return (
    <div>
      <PageHead title="Statements" sub="Import a wholesaler's monthly statement and check it against what you hold." />
      <RefLinks refresh={linksKey} onChanged={list.reload} />
      <div className="grid gap-6 lg:grid-cols-5">
        <form onSubmit={submit} className="space-y-4 rounded-3xl border border-slate-200 bg-white p-5 lg:col-span-2 rise" data-testid="statement-import-form">
          <h2 className="text-xl font-bold">Import & reconcile</h2>
          <div className="space-y-1.5"><Label>Wholesaler</Label>
            <Input list="stmt-suppliers" required value={supplier} onChange={(e) => setSupplier(e.target.value)} placeholder="Wholesaler name" data-testid="statement-supplier-input" />
            <datalist id="stmt-suppliers">{(suppliers.data || []).map((s) => <option key={s.name} value={s.name} />)}</datalist>
          </div>
          <div className="space-y-1.5"><Label>Statement month</Label>
            <Input type="month" required value={month} onChange={(e) => setMonth(e.target.value)} data-testid="statement-month-input" />
          </div>
          <div className="space-y-1.5"><Label>Statement file</Label>
            <Input type="file" accept=".csv,text/csv,.pdf,application/pdf,image/*" onChange={pickFile} data-testid="csv-upload-input" />
            <p className="text-xs text-slate-500">PDF or photo: the lines are read for you to check. CSV: columns <code>type,ref,amount</code> (INV or CRN, amount in pounds). Re-importing a month replaces it.</p>
          </div>
          {reading && <p className="flex items-center gap-2 text-sm font-medium text-teal-700" data-testid="statement-reading-status"><Loader2 className="h-4 w-4 animate-spin" /> Reading the statement…</p>}
          {lines && <StatementLines lines={lines} setLines={setLines} />}
          <Button type="submit" disabled={busy || reading} className="w-full" data-testid="reconcile-run-button">
            {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />} Import & email queries
          </Button>
          <Result r={result} />
        </form>
        <div className="lg:col-span-3">
          {list.data?.length === 0 && <Empty icon={FileSpreadsheet} title="No statements yet" text="Imported statements show here." testid="statements-empty" />}
          <div className="space-y-2" data-testid="statements-list">
            {(list.data || []).map((s) => (
              <div key={s.supplier + s.month} className="flex flex-col gap-3 rounded-2xl border border-slate-200 bg-white p-4 sm:flex-row sm:items-center" data-testid={`statement-row-${s.supplier}-${s.month}`}>
                <div className="flex-1">
                  <p className="font-semibold">{s.supplier} <span className="font-mono text-sm text-slate-500">{s.month}</span></p>
                  <p className="text-xs text-slate-500">{s.rows} rows · invoices {gbp(s.invoice_total)} · credits {gbp(s.credit_total)} · {s.last_reconciled ? `reconciled ${fmtDate(s.last_reconciled)}` : "not reconciled"}</p>
                  <Coverage s={s} />
                </div>
                <span className={`text-xs font-semibold ${s.open_queries ? "text-amber-700" : "text-emerald-700"}`}>{s.open_queries} open</span>
                <Button size="sm" variant="outline" onClick={() => reconcile(s.supplier, s.month).catch((e) => toast.error(e.message))} data-testid={`rereconcile-${s.supplier}-${s.month}`}>
                  <RefreshCw className="mr-1 h-3.5 w-3.5" /> Reconcile
                </Button>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
